import { StyleSheet, css } from 'aphrodite';
import { useId, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../../web/components-v2/Button';
import { Checkbox } from '../../../web/components-v2/Checkbox';
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '../../../web/components-v2/Field';
import { InputGroupAddon, InputGroupInput, InputGroupText, InputGroupV2 } from '../../../web/components-v2/InputGroup';
import { SPACE, THEME } from '../../../web/components-v2/Theme/theme';
import { TextArea } from '../../../web/components-v2/TextArea';
import { apiErrorMessage } from '../../focused-view/data/apiErrorMessage';
import { ITimeEntry, useSaveTimeEntryHoursMutation, useSubmitTimeEntryMutation } from '../../queries/TimeEntry';
import { parseHoursInput } from '../hours';

const SUBMIT_NOTE_MAX_LENGTH = 1000;

export function HoursEntryForm({
	day,
	entry,
	canActAsSelf,
	onCancel,
	onSubmitted,
}: {
	day: string;
	entry?: ITimeEntry;
	canActAsSelf: boolean;
	onCancel?: () => void;
	onSubmitted?: () => void;
}) {
	const id = useId();
	const hoursId = `${id}-hours`;
	const attestId = `${id}-attest`;
	const noteId = `${id}-note`;
	const [hoursText, setHoursText] = useState(entry?.hours === undefined ? '' : String(entry.hours));
	const [attested, setAttested] = useState(false);
	const [note, setNote] = useState(entry?.submitNote ?? '');
	const [touched, setTouched] = useState(false);

	const parsed = parseHoursInput(hoursText);
	const showError = touched && !!parsed.error;
	const isResubmit = entry?.status === 'Submitted' || entry?.status === 'Rejected';
	const canSaveDraft = entry?.status !== 'Submitted';

	const saveHours = useSaveTimeEntryHoursMutation({
		onError: err => toast.error(apiErrorMessage(err, 'Failed to save hours')),
		onSuccess: () => toast.success('Hours saved. Submit them when you’re ready.'),
	});
	const submitHours = useSubmitTimeEntryMutation({
		onError: err => toast.error(apiErrorMessage(err, 'Failed to submit hours')),
		onSuccess: () => {
			toast.success('Hours submitted for review');
			setAttested(false);
			onSubmitted?.();
		},
	});
	const isPending = saveHours.isPending || submitHours.isPending;

	const onSave = () => {
		setTouched(true);
		if (parsed.hours !== undefined) {
			saveHours.mutate({ day, hours: parsed.hours });
		}
	};

	const onSubmit = () => {
		setTouched(true);
		if (parsed.hours !== undefined && attested) {
			submitHours.mutate({ attested: true, day, hours: parsed.hours, note: note.trim() || undefined });
		}
	};

	return (
		<div className={css(styles.form)}>
			<FieldGroup>
				<Field data-invalid={showError || undefined}>
					<FieldLabel htmlFor={hoursId}>Hours worked</FieldLabel>
					<div className={css(styles.hoursInput)}>
						<InputGroupV2>
							<InputGroupInput
								id={hoursId}
								inputMode='decimal'
								value={hoursText}
								aria-invalid={showError || undefined}
								disabled={!canActAsSelf}
								onBlur={() => setTouched(true)}
								onChange={e => setHoursText(e.target.value)}
							/>
							<InputGroupAddon align='inlineEnd'>
								<InputGroupText>hours</InputGroupText>
							</InputGroupAddon>
						</InputGroupV2>
					</div>
					{showError ? (
						<FieldError>{parsed.error} We don’t round for you.</FieldError>
					) : (
						<FieldDescription>Quarter hours only, like 7.75, 8 or 8.25. No breaks to track.</FieldDescription>
					)}
				</Field>
				<Field>
					<FieldLabel htmlFor={noteId}>Note for your manager (optional)</FieldLabel>
					<TextArea
						id={noteId}
						rows={3}
						maxLength={SUBMIT_NOTE_MAX_LENGTH}
						value={note}
						disabled={!canActAsSelf}
						onChange={e => setNote(e.target.value)}
					/>
					<FieldDescription>
						Anything they should know about these hours. Sent only when you submit; Save for later doesn’t keep it.
					</FieldDescription>
				</Field>
				<Field orientation='horizontal'>
					<Checkbox
						id={attestId}
						checked={attested}
						disabled={!canActAsSelf}
						onCheckedChange={checked => setAttested(checked === true)}
					/>
					<FieldLabel htmlFor={attestId}>I certify these hours are accurate</FieldLabel>
				</Field>
			</FieldGroup>
			{!canActAsSelf ? <p className={css(styles.note)}>Only the rep can enter and submit their own hours.</p> : null}
			<div className={css(styles.actions)}>
				{onCancel ? (
					<Button type='button' variant='outline' onClick={onCancel}>
						Cancel
					</Button>
				) : null}
				{canSaveDraft ? (
					<Button type='button' variant='outline' disabled={!canActAsSelf || isPending} onClick={onSave}>
						Save for later
					</Button>
				) : null}
				<Button
					type='button'
					variant='primary'
					disabled={!canActAsSelf || !attested || isPending}
					isLoading={submitHours.isPending}
					onClick={onSubmit}
				>
					{isResubmit ? 'Resubmit hours' : 'Submit hours'}
				</Button>
			</div>
		</div>
	);
}

const styles = StyleSheet.create({
	actions: {
		display: 'flex',
		gap: SPACE[8],
		justifyContent: 'flex-end',
	},
	form: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[20],
	},
	hoursInput: {
		width: 160,
	},
	note: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.xs,
		margin: 0,
	},
});
